import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, CreditCard, Pencil } from 'lucide-react'
import { getContentList } from '../../lib/api/content'
import { useToggleEnabled } from '../conventions/useToggleEnabled'
import { PageHead, Stat, Switch } from './ui'
import { cn } from '../../lib/cn'
import { PaymentModeEditDrawer } from './PaymentModeEditDrawer'

type El = Record<string, unknown>
const isYes = (v: unknown) => v === true || v === 'true' || v === 1 || v === '1'

const LIST_KEY = ['content', 'paymentMode', 'all'] as const
type Row = { id: string; name: string; type: string; cta: string; enabled: boolean }

export function PaymentModesPage() {
  const [editId, setEditId] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const list = useQuery({
    queryKey: LIST_KEY,
    queryFn: () => getContentList('paymentMode', { limit: 100 }),
  })
  const toggle = useToggleEnabled('paymentMode', LIST_KEY)

  const rows = useMemo<Row[]>(() => {
    return (list.data?.data ?? [])
      .map((r) => {
        const el = r.elements as El
        return {
          id: r.id,
          name: String(el.name ?? el.label ?? 'Senza nome'),
          type: String(el.type ?? '').toUpperCase(),
          cta: String(el.ctaLabel ?? ''),
          enabled: isYes(el.enabled),
        }
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'it'))
  }, [list.data])

  const stats = useMemo(() => ({ total: rows.length, active: rows.filter((r) => r.enabled).length }), [rows])

  function flip(r: Row) {
    setBusyId(r.id)
    toggle.mutate({ id: r.id, enabled: !r.enabled }, { onSettled: () => setBusyId(null) })
  }

  return (
    <div className="w-full px-6 pb-16 pt-7 lg:px-9">
      <PageHead
        title="Configurazione · Metodi di pagamento"
        description="Modalità di pagamento accettate online e alla cassa. Attiva/disattiva un metodo o clicca una riga per impostarne la disponibilità (giorni/orari) e i testi."
        model="paymentMode"
      />

      <div className="mb-[18px] grid grid-cols-2 gap-[14px]">
        <Stat label="Metodi" value={String(stats.total)} sub="configurati" />
        <Stat label="Attivi" value={String(stats.active)} sub="accettati ora" />
      </div>

      <p className="mb-2 flex items-center gap-1.5 text-[12px] text-muted">
        <Pencil className="h-3.5 w-3.5" /> Clicca una riga per modificare · usa l’interruttore per attivare/disattivare
      </p>

      {list.isError ? (
        <div className="cv-panel px-4 py-16 text-center text-sm text-muted">
          <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare i metodi di pagamento.
        </div>
      ) : list.isLoading ? (
        <div className="cv-panel divide-y divide-line-soft">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="px-5 py-4">
              <div className="skeleton h-4 w-48 rounded-sm bg-line-soft" />
            </div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="cv-panel px-4 py-16 text-center text-sm text-muted">Nessun metodo di pagamento configurato.</div>
      ) : (
        <div className="cv-panel overflow-hidden">
          {rows.map((r) => (
            <div
              key={r.id}
              onClick={() => setEditId(r.id)}
              className={cn(
                'flex cursor-pointer items-center gap-4 border-b border-line-soft px-5 py-4 last:border-0 hover:bg-paper',
                !r.enabled && 'opacity-60',
              )}
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-paper text-slate ring-1 ring-line">
                <CreditCard className="h-[18px] w-[18px]" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-coal">{r.name}</span>
                  {r.type && (
                    <span className="rounded-md bg-paper px-1.5 py-0.5 font-mono text-[10.5px] font-semibold tracking-wide text-muted ring-1 ring-line">
                      {r.type}
                    </span>
                  )}
                </div>
                {r.cta && <div className="mt-0.5 truncate text-[12.5px] text-muted">CTA: “{r.cta}”</div>}
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                <Switch on={r.enabled} busy={busyId === r.id} onClick={() => flip(r)} label={`Attiva ${r.name}`} />
              </div>
            </div>
          ))}
        </div>
      )}

      <PaymentModeEditDrawer paymentModeId={editId} onClose={() => setEditId(null)} />
    </div>
  )
}
